import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Lock, DeviceMobile, Key } from "@phosphor-icons/react";

const AUTH_BG =
  "https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjeWJlciUyMHNlY3VyaXR5JTIwbmV0d29yayUyMGRhcmslMjBibHVlfGVufDB8fHx8MTc4NTU1MjI3Nnww&ixlib=rb-4.1.0&q=85";

// phases: password | change_password | totp_setup | totp_verify
export default function Login() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState("");
  const [prov, setProv] = useState(null); // {secret, otpauth_uri, qr}
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fail = (e) => setError(formatApiError(e.response?.data?.detail) || e.message);

  const applyStep = (data) => {
    setPending(data.pending_token);
    if (data.must_change_password) setPhase("change_password");
    else if (data.totp_setup_required) { setProv(data.provisioning); setPhase("totp_setup"); }
    else if (data.totp_required) setPhase("totp_verify");
  };

  const doPassword = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyStep(data);
    } catch (e) { fail(e); } finally { setLoading(false); }
  };

  const doChangePassword = async (e) => {
    e.preventDefault(); setError("");
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", { pending_token: pending, new_password: newPassword, confirm_password: confirmPassword });
      setConfirmPassword(""); setNewPassword("");
      applyStep(data);
    } catch (e) { fail(e); } finally { setLoading(false); }
  };

  const doSetup = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/setup-2fa", { pending_token: pending, code });
      completeLogin(data.token, data.user);
      navigate("/");
    } catch (e) { fail(e); } finally { setLoading(false); }
  };

  const doVerify = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", { pending_token: pending, code });
      completeLogin(data.token, data.user);
      navigate("/");
    } catch (e) { fail(e); } finally { setLoading(false); }
  };

  const ErrorBox = () =>
    error ? (
      <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
        {error}
      </div>
    ) : null;

  const codeInput = (
    <div>
      <Label htmlFor="code" className="text-slate-300">6-digit code</Label>
      <Input id="code" data-testid="totp-code-input" inputMode="numeric" maxLength={6} value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
        className="mt-1.5 bg-[#0f172a] border-[#1e293b] text-white font-mono tracking-[0.4em] text-center text-lg" required />
    </div>
  );

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#020817]">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center gap-3 mb-10">
            <Logo size={44} />
            <div>
              <div className="font-head text-2xl font-extrabold tracking-tight text-white">SoverenX</div>
              <div className="overline text-slate-500">Systems · Pentest Platform</div>
            </div>
          </div>

          {phase === "password" && (
            <>
              <h1 className="font-head text-3xl font-bold tracking-tight text-white">Secure access</h1>
              <p className="text-sm text-slate-400 mt-2 mb-8">Sign in to the sovereign console. All scan data remains in Australia.</p>
              <form onSubmit={doPassword} className="space-y-4" autoComplete="off">
                <div>
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input id="email" data-testid="login-email-input" type="email" value={email} autoComplete="off"
                    placeholder="you@company.com"
                    onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-[#0f172a] border-[#1e293b] text-white" required />
                </div>
                <div>
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <Input id="password" data-testid="login-password-input" type="password" value={password} autoComplete="new-password"
                    placeholder="Enter your password"
                    onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-[#0f172a] border-[#1e293b] text-white" required />
                </div>
                <ErrorBox />
                <Button data-testid="login-submit-button" type="submit" disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md h-11">
                  <Lock size={17} weight="bold" className="mr-2" />{loading ? "Authenticating…" : "Continue"}
                </Button>
                <button type="button" data-testid="forgot-password-link" onClick={() => setShowForgot((v) => !v)}
                  className="w-full text-center text-sm text-slate-500 hover:text-slate-300 transition-colors duration-150">
                  Forgot password?
                </button>
                {showForgot && (
                  <div data-testid="forgot-password-message" className="text-sm text-slate-300 bg-[#0f172a] border border-[#1e293b] rounded-md px-3 py-2.5">
                    No self-service reset — password resets are handled by your administrator. Contact{" "}
                    <a
                      data-testid="admin-mailto-link"
                      href="mailto:info@soverenxsystems.com.au?subject=SoverenX%20password%20reset%20request"
                      className="text-blue-400 font-mono underline underline-offset-2 hover:text-blue-300"
                    >
                      info@soverenxsystems.com.au
                    </a>{" "}
                    to have your password reset. They'll issue a new temporary password for you.
                  </div>
                )}
              </form>
            </>
          )}

          {phase === "change_password" && (
            <>
              <div className="flex items-center gap-2 text-blue-400 mb-2"><Key size={20} weight="bold" /><span className="overline">First sign-in</span></div>
              <h1 className="font-head text-3xl font-bold tracking-tight text-white">Set a new password</h1>
              <p className="text-sm text-slate-400 mt-2 mb-8">You're using a temporary password. Choose a new one to continue.</p>
              <form onSubmit={doChangePassword} className="space-y-4" autoComplete="off">
                <div>
                  <Label htmlFor="np" className="text-slate-300">New password</Label>
                  <Input id="np" data-testid="new-password-input" type="password" value={newPassword} autoComplete="new-password"
                    onChange={(e) => setNewPassword(e.target.value)} className="mt-1.5 bg-[#0f172a] border-[#1e293b] text-white"
                    placeholder="At least 8 characters" required />
                </div>
                <div>
                  <Label htmlFor="cp" className="text-slate-300">Confirm new password</Label>
                  <Input id="cp" data-testid="confirm-password-input" type="password" value={confirmPassword} autoComplete="new-password"
                    onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1.5 bg-[#0f172a] border-[#1e293b] text-white"
                    placeholder="Re-enter new password" required />
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-amber-400">Passwords do not match yet.</p>
                )}
                <ErrorBox />
                <Button data-testid="change-password-submit" type="submit"
                  disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md h-11">
                  {loading ? "Saving…" : "Set password & continue"}
                </Button>
              </form>
            </>
          )}

          {phase === "totp_setup" && (
            <>
              <div className="flex items-center gap-2 text-blue-400 mb-2"><DeviceMobile size={20} weight="bold" /><span className="overline">Two-factor setup</span></div>
              <h1 className="font-head text-3xl font-bold tracking-tight text-white">Protect your account</h1>
              <p className="text-sm text-slate-400 mt-2 mb-5">Scan this QR with Google or Microsoft Authenticator, then enter the 6-digit code.</p>
              {prov?.qr && (
                <div className="flex flex-col items-center gap-3 mb-5">
                  <img data-testid="totp-qr" src={prov.qr} alt="2FA QR code" className="h-44 w-44 rounded-lg border border-[#1e293b] bg-white p-2" />
                  <div className="text-xs text-slate-500">Or enter key manually:</div>
                  <code className="text-xs text-blue-300 bg-[#0f172a] border border-[#1e293b] rounded px-2 py-1 break-all">{prov.secret}</code>
                </div>
              )}
              <form onSubmit={doSetup} className="space-y-4">
                {codeInput}
                <ErrorBox />
                <Button data-testid="totp-setup-submit" type="submit" disabled={loading || code.length !== 6}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md h-11">
                  {loading ? "Verifying…" : "Verify & enter console"}
                </Button>
              </form>
            </>
          )}

          {phase === "totp_verify" && (
            <>
              <div className="flex items-center gap-2 text-blue-400 mb-2"><DeviceMobile size={20} weight="bold" /><span className="overline">Two-factor</span></div>
              <h1 className="font-head text-3xl font-bold tracking-tight text-white">Enter your code</h1>
              <p className="text-sm text-slate-400 mt-2 mb-8">Open your authenticator app and enter the current 6-digit code.</p>
              <form onSubmit={doVerify} className="space-y-4">
                {codeInput}
                <ErrorBox />
                <Button data-testid="totp-verify-submit" type="submit" disabled={loading || code.length !== 6}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md h-11">
                  <ShieldCheck size={17} weight="bold" className="mr-2" />{loading ? "Verifying…" : "Sign in"}
                </Button>
              </form>
            </>
          )}

          <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck size={15} weight="fill" className="text-blue-500" />
            Owner-only access · authenticator 2FA enforced · data in Australia
          </div>
        </div>
      </div>

      <div className="relative hidden md:block">
        <img src={AUTH_BG} alt="Network security" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#020817]/70" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#020817] via-transparent to-blue-900/20" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="overline text-blue-400 mb-3">Sovereign · Contained · Auditable</div>
          <h2 className="font-head text-3xl font-bold text-white leading-tight max-w-md">
            Discovery, vulnerability scanning &amp; controlled exploitation — all in-region.
          </h2>
          <p className="text-slate-400 mt-4 max-w-md text-sm">
            Nmap &amp; OpenVAS engines run server-side inside your Azure Australia tenant. No scan data ever leaves the environment.
          </p>
        </div>
      </div>
    </div>
  );
}
